// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import { playCircleString } from "@sc/XDL/Icons/PlayCircle";
import React, { useEffect, useRef, useState } from "react";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import styled, { css } from "styled-components";

const enum fields {
    videoType = "videoType",
    videoURL = "videoURL",
    videoParameters = "videoParameters",
    title = "title",
    description = "description",
    uploadDate = "uploadDate",
    duration = "duration",
}

interface YoutubeVideoProps extends WidgetProps {
    fields: {
        [fields.videoType]: string;
        [fields.videoURL]: string;
        [fields.videoParameters]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.uploadDate]: string;
        [fields.duration]: string;
    };
}

export const videoWrapperCommonStyle = css`
    position: relative;
    padding-bottom: 56.25%;
    padding-top: 0;
    height: 0;
    overflow: hidden;

    iframe,
    object,
    embed {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
`;

const VimeoVideoWrapper = styled.div`
    ${videoWrapperCommonStyle}
`;

const YoutubeVideoWrapper = styled.div`
    ${videoWrapperCommonStyle}

    /* lite youtube embed styling */
    .yt-lite {
        background-color: ${getColor("common.black")};
        position: relative;
        display: block;
        contain: content;
        background-position: 50%;
        background-size: cover;
        cursor: pointer;
    }
    .yt-lite.lyt-activated::before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAADGCAYAAAAT+OqFAAAAdklEQVQoz42QQQ7AIAgEF/T/D+kbq/RWAlnQyyazA4aoAB4FsBSA/bFjuF1EOL7VbrIrBuusmrt4ZZORfb6ehbWdnRHEIiITaEUKa5EJqUakRSaEYBJSCY2dEstQY7AuxahwXFrvZmWl2rh4JZ07z9dLtesfNj5q0FU3A5ObbwAAAABJRU5ErkJggg==);
        background-position: top;
        background-repeat: repeat-x;
        height: 60px;
        padding-bottom: 50px;
        width: 100%;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite::after {
        content: "";
        display: block;
        padding-bottom: var(--aspect-ratio);
    }
    .yt-lite > iframe {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }
    .yt-lite > .lty-playbtn {
        width: 75px;
        height: 75px;
        cursor: pointer;
        background-color: ${getColor("common.white")};
        -webkit-mask-image: url("data:image/svg+xml,${playCircleString}");
        mask-image: url("data:image/svg+xml,${playCircleString}");
        mask-size: 100%;
        background-repeat: no-repeat;
        background-size: 100%;
        border: none;
        z-index: 1;
        opacity: 1;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite:before {
        content: "";
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: ${getColor("secondary")};
        opacity: 0.25;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite:hover:before {
        opacity: 0.4;
    }
    .yt-lite:hover > .lty-playbtn {
        width: 80px;
        height: 80px;
    }
    .yt-lite > .lty-playbtn,
    .yt-lite > .lty-playbtn::before {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
    }
    .yt-lite.lyt-activated {
        cursor: unset;
    }
    .yt-lite.lyt-activated::before,
    .yt-lite.lyt-activated > .lty-playbtn {
        opacity: 0;
        pointer-events: none;
    }
`;

const videoTemplateWidget = ({
    fields: { videoType, videoURL, videoParameters, title, description, uploadDate, duration },
}: YoutubeVideoProps) => {
    const [videoId, setVideoId] = useState("");

    const getYouTubeId = (url: string) => {
        const array = url.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
        const Id = array[2] !== undefined ? array[2].split(/[^0-9a-z_\-]/i)[0] : array[0];
        setVideoId(Id);
    };

    const getVimeoId = (url: string) => {
        let Id = "";
        try {
            Id = new URL(url)?.pathname.match(/[0-9]/g)?.join("") || "";
        } catch {
            Id = url.match(/[0-9]/g)?.join("") || "";
        }
        setVideoId(Id);
    };

    useEffect(() => {
        videoType === "youtube" ? getYouTubeId(videoURL) : getVimeoId(videoURL);
    }, []);

    return (
        <>
            {videoType === "youtube" ? (
                <YoutubeVideoWrapper>
                    {videoParameters.length > 0 ? (
                        <iframe
                            src={`https://www.youtube.com/embed/${videoId}?${videoParameters}`}
                            frameBorder="0"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title={title}
                        />
                    ) : (
                        <LiteYouTubeEmbed id={videoId} title={title} params={videoParameters} />
                    )}
                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID="https://www.youtube.com" itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={`https://www.youtube.com/embed/${videoId}`} />
                            <meta itemProp="contentUrl" content={`https://www.youtube.com/embed/${videoId}`} />
                            <meta
                                itemProp="thumbnailUrl"
                                content={`http://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                            />
                            <meta itemProp="name" content={title} />
                            <meta itemProp="description" content={description} />
                            <meta itemProp="caption" content={description} />
                            <meta itemProp="uploadDate" content={uploadDate} />
                            <meta itemProp="duration" content={duration} />
                        </div>
                    </div>
                </YoutubeVideoWrapper>
            ) : (
                <VimeoVideoWrapper>
                    <iframe
                        src={`https://player.vimeo.com/video/${videoId}?color=ff214f&byline=0&portrait=0${videoParameters}`}
                        allow="autoplay; fullscreen; picture-in-picture"
                        frameBorder="0"
                    ></iframe>

                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID="https://www.vimeo.com" itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={`https://vimeo.com/${videoId}`} />
                            <meta itemProp="contentUrl" content={`https://vimeo.com/${videoId}`} />
                            <meta itemProp="thumbnailUrl" content={`https://vumbnail.com/${videoId}.jpg`} />
                            <meta itemProp="name" content={title} />
                            <meta itemProp="description" content={description} />
                            <meta itemProp="caption" content={description} />
                            <meta itemProp="uploadDate" content={uploadDate} />
                            <meta itemProp="duration" content={duration} />
                        </div>
                    </div>
                </VimeoVideoWrapper>
            )}
        </>
    );
};

const metadataMsg = "will be used to add metadata for this video.";

export const definition: WidgetDefinition = {
    group: XNGAGE_CUSTOM_WIDGET,
    fieldDefinitions: [
        {
            name: fields.videoType,
            displayName: "Video Type",
            editorTemplate: "DropDownField",
            defaultValue: "youtube",
            options: [
                {
                    displayName: "Youtube",
                    value: "youtube",
                },
                {
                    displayName: "Vimeo",
                    value: "vimeo",
                },
            ],
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.videoURL,
            displayName: "Video URL or Id",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.videoParameters,
            displayName: "Video Parameters",
            editorTemplate: "TextField",
            defaultValue: "",
            tooltip: `e.g: "start=1150&other=value"`,
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.uploadDate,
            displayName: "Upload Date",
            editorTemplate: "DateTimeField",
            defaultValue: new Date(),
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.duration,
            displayName: "Duration",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: videoTemplateWidget,
    definition,
};

export default widgetModule;
